import { SummaryCards_CheckoutConfigFragment } from "src/pages/configurator/components/SummaryCards.generated";
import { AddAduOptionCode } from "src/pages/configurator/stepTemplates/Adu.step";
import { CheckoutConfigOptionGroupStep } from "~generated/graphqlTypes";
import { getProgramDataStrings } from "./plan.utils";
import {
  SummaryPrint_CheckoutConfigOptionGroupFragment,
  SummaryUtils_CheckoutConfigOptionGroupFragment,
} from "./summary.utils.generated";

export function getSummaryPlanDescription(
  checkoutConfig: Pick<SummaryCards_CheckoutConfigFragment, "productOffering" | "programData" | "isReversed">,
): {
  planName: string;
  planProgramData: string;
} {
  const { productOffering, programData, isReversed } = checkoutConfig;

  const { bedrooms, bathrooms, sqft } = getProgramDataStrings(programData);

  if (!productOffering) {
    return { planName: "", planProgramData: "" };
  }

  return {
    planName: `${productOffering.name} (Orientation: ${isReversed ? "Reversed" : "Standard"})`,
    planProgramData: `${bedrooms}, ${bathrooms} & ${sqft}`,
  };
}

export function filterSelectedOptionsBySteps(
  optionGroups: SummaryUtils_CheckoutConfigOptionGroupFragment[],
  steps: CheckoutConfigOptionGroupStep[],
) {
  return optionGroups
    .filter((ccog) => ccog.isEnabled && steps.includes(ccog.step))
    .map((ccog) => ccog.selectedOption)
    .compact();
}

// ----- Effective summary helpers -----
// effective summary items are items that may have been part of the base config but were deselected by the user or
// were optional option groups that were not selected by the user.
export function filterSummaryEffectiveOptionGroups(
  optionGroups: SummaryUtils_CheckoutConfigOptionGroupFragment[],
  steps: CheckoutConfigOptionGroupStep[],
) {
  return optionGroups.filter((ccog) => ccog.isEnabled && steps.includes(ccog.step) && !!ccog.selectedOption);
}

export function getSummaryGroupsEffectivePriceInCents(
  optionGroups: SummaryUtils_CheckoutConfigOptionGroupFragment[],
): number {
  return optionGroups.sum((ccog) => ccog.selectedOption?.totalPriceInCents);
}

/**
 * Finds all downstream option groups included by the given option group's selected option by checking allOptionGroups entry.includedByRoot.id
 */
export function getDownstreamIncludedOptionGroups(
  optionGroup: SummaryPrint_CheckoutConfigOptionGroupFragment,
  allOptionGroups: SummaryPrint_CheckoutConfigOptionGroupFragment[],
): SummaryPrint_CheckoutConfigOptionGroupFragment[] {
  const selectedOptionId = optionGroup.selectedOption?.id;
  if (!selectedOptionId) return [];

  // Find all groups whose selectedOption.includedByRoot points to our selected option
  // Note: we skip the `isEnabled` check intentionally to ensure that anything automatically applied to the config is caught
  // and shown on the summary. Much like how our summary step Interior Personalization section only cares about having a selected option.
  const includedGroups = allOptionGroups.filter((ccog) => ccog.selectedOption?.includedByRoot?.id === selectedOptionId);
  return includedGroups;
}

/** Checks whether any option group has the ADU add-on selected. ADU *everything* should have this as a pre-req */
export function legacyHasSelectedAduOption(
  optionGroups: Pick<SummaryUtils_CheckoutConfigOptionGroupFragment, "selectedOption">[],
): boolean {
  // Note: Checking the option code instead of AduStep + hasSelectedOption because of a bug where some
  // linked personalization options are being shown on the ADU step instead of Addt'l options where they have a selected
  // option applied by the BE defaultIf logic.
  return optionGroups.some((ccog) => ccog.selectedOption?.option.code === AddAduOptionCode);
}

export function buildSummaryListEffectiveLineItems(
  optionGroups: SummaryUtils_CheckoutConfigOptionGroupFragment[],
  // if true, will include the location name in the title.
  withLocation: boolean = false,
): { title: string; priceInCents: number; optionCode: string }[] {
  return optionGroups
    .map((ccog) => {
      // Display the selected option
      const option = ccog.selectedOption?.option;
      if (!option) return null;
      // get location name if applicable
      const locationName =
        withLocation && ccog.group.readyPlanLocation ? ` - ${ccog.group.readyPlanLocation.marketingName}` : "";
      // get price in cents
      const priceInCents = ccog.selectedOption?.totalPriceInCents;
      return {
        title: `${option?.name}${locationName}${ccog.selectedOption ? "" : " (removed)"}`,
        priceInCents: priceInCents ?? 0,
        optionCode: option?.code,
      };
    })
    .compact();
}
